import { FC } from 'react';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { Grid, GridProps, BoxProps, ListItem, Text } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { TeaserCard } from './Teaser';
import { AgencyProfileProps } from '@/types/agency';
import { urls } from '@/lib/urls';
import ContactInfoStack, {
  getContactInfoStackItems,
} from '@/components/common/ContactInfoStack';
import { getLocalizedRegionNames } from '@/lib/regions';

const ShowMoreSection = dynamic(
  () => import('@/components/frontend/ShowMoreSection'),
);
const UserAvatar = dynamic(() => import('@/components/common/UserAvatar'));
const OrderedList = dynamic(() => import('@/components/frontend/OrderedList'));

const RealtorGrid: FC<GridProps> = (props) => (
  <Grid layerStyle="grid" as={OrderedList} {...props} />
);

const getLoadMoreContainer =
  (limit: true | number): typeof ShowMoreSection =>
  (props) =>
    (
      <ShowMoreSection
        {...props}
        as={RealtorGrid}
        limit={typeof limit === 'boolean' ? undefined : limit}
      />
    );

// Limit: true renders LoadMore with default limit/step
const Realtors = ({
  users,
  limit: givenLimit,
  ...props
}: Pick<AgencyProfileProps, 'users'> &
  BoxProps & { limit?: true | string | number }) => {
  const { t } = useTranslation('frontoffice');
  const { locale } = useRouter();
  const limit =
    typeof givenLimit === 'string'
      ? Number.parseInt(givenLimit, 10)
      : givenLimit;

  const Component = limit ? getLoadMoreContainer(limit) : RealtorGrid;

  return (
    <Component {...props}>
      {users.map(
        ({
          id,
          name,
          contact_email,
          title,
          phone_number: phone,
          avatar_attachment,
          working_area: areas,
        }) =>
          !name ? null : (
            <TeaserCard
              heading={{ title: name, variant: 'subtitle' }}
              key={`${name}-${id}`}
              link={{
                url: urls.public.realtors.get({
                  id,
                  name: name || 'valittaja',
                }),
                title: t('get_to_know', 'Tutustu'),
                altTitle: `${t('get_to_know', 'Tutustu')} ${name}`,
              }}
              meta={
                <UserAvatar
                  size="xl"
                  user={{ name, avatar_attachment }}
                  marginBottom={4}
                />
              }
              as={ListItem}
              display="flex"
            >
              {title ? (
                <Text marginBottom="4" textStyle="sm">
                  {title}
                </Text>
              ) : null}
              <ContactInfoStack
                items={getContactInfoStackItems({
                  email: contact_email,
                  phone,
                  areas: getLocalizedRegionNames(
                    areas as string[],
                    locale,
                  ).join(', '),
                })}
                displayLinks
              />
            </TeaserCard>
          ),
      )}
    </Component>
  );
};

export default Realtors;
